import { Link } from 'react-router-dom'
import { Briefcase, ExternalLink, Newspaper, ShieldAlert } from 'lucide-react'
import { ChannelBadge } from '@/features/transactions/components/ChannelBadge'
import { PriorityDot } from '../queue/PriorityDot'
import { StatusIcon } from '../queue/StatusIcon'
import { ScoreCell } from '../queue/ScoreCell'
import { CopyableField } from '@/features/transactions/components/CopyableField'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { AlertDetail } from '../../types'

const PRIORITY_LABEL = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
} as const

/**
 * AF-126 — top of the detail page. Single row of context with the most
 * decision-relevant facts visible at a glance.
 */
export function AlertHeader({ alert }: { alert: AlertDetail }) {
  return (
    <header className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-lg font-semibold tabular-nums">{alert.alert_number}</h1>
        <span className="flex items-center gap-1.5 rounded-sm bg-muted px-2 py-0.5 text-xs">
          <PriorityDot priority={alert.priority} />
          <span className="font-medium">{PRIORITY_LABEL[alert.priority]}</span>
        </span>
        <span className="flex items-center gap-1.5 text-sm">
          <StatusIcon status={alert.status} />
          <span className="capitalize">{alert.status.replace('_', ' ')}</span>
        </span>
        <ChannelBadge channel={alert.channel} />
        <div className="flex items-center gap-1 text-sm">
          <span className="text-muted-fg">Score</span>
          <ScoreCell score={alert.score} />
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-4 text-sm">
        <span className="text-muted-fg">{alert.scenario_code}</span>
        <span className="font-medium text-fg">{alert.scenario_name}</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-fg">
        <span>Opened {formatDateTime(alert.created_at)}</span>
        <span>·</span>
        <span>Queue: {alert.queue_name}</span>
        <span>·</span>
        <span>
          Customer:{' '}
          <CopyableField value={alert.customer.customer_number} mono className="text-muted-fg" />
        </span>
      </div>

      {alert.case_id && alert.case_number && (
        <Link
          to={`/cases/${alert.case_id}`}
          className="flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs transition-colors hover:border-primary"
          title={`Open case ${alert.case_number}`}
        >
          <span className="flex items-center gap-2 text-fg">
            <Briefcase size={12} className="shrink-0 text-primary" aria-hidden />
            <span className="font-medium">Linked case</span>
            <span className="font-mono tabular-nums text-primary">
              {alert.case_number}
            </span>
          </span>
          <span className="flex items-center gap-1 text-primary">
            Open case
            <ExternalLink size={11} aria-hidden />
          </span>
        </Link>
      )}

      {alert.adverse_media && (
        <Link
          to={`/adverse-media/classifications/${alert.adverse_media.public_id}`}
          className={cn(
            'flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-xs transition-colors',
            // Tint by severity so the chip's severity matches the
            // adverse-media verdict at a glance.
            alert.adverse_media.severity === 'critical' || alert.adverse_media.severity === 'high'
              ? 'border-status-failed/40 bg-status-failed/5 hover:border-status-failed'
              : alert.adverse_media.severity === 'medium'
                ? 'border-status-pending/40 bg-status-pending/5 hover:border-status-pending'
                : 'border-border bg-muted hover:border-fg/40',
          )}
          title={
            alert.adverse_media.article_title
              ? `Open adverse media — ${alert.adverse_media.article_title}`
              : 'Open originating adverse-media classification'
          }
        >
          <span className="flex flex-wrap items-center gap-2 text-fg">
            <Newspaper
              size={12}
              className={cn(
                'shrink-0',
                alert.adverse_media.severity === 'critical' || alert.adverse_media.severity === 'high'
                  ? 'text-status-failed'
                  : alert.adverse_media.severity === 'medium'
                    ? 'text-status-pending'
                    : 'text-muted-fg',
              )}
              aria-hidden
            />
            <span className="font-medium">Adverse media</span>
            {alert.adverse_media.category && (
              <span className="font-mono text-muted-fg">{alert.adverse_media.category}</span>
            )}
            <span className="capitalize">
              {alert.adverse_media.severity} ·{' '}
              <span className="tabular-nums">
                {(alert.adverse_media.confidence * 100).toFixed(1)}%
              </span>
            </span>
            {alert.adverse_media.source_credibility_tier && (
              <span className="text-muted-fg">{alert.adverse_media.source_credibility_tier}</span>
            )}
            {alert.adverse_media.subject_name && (
              <span className="text-muted-fg">— {alert.adverse_media.subject_name}</span>
            )}
          </span>
          <span className="flex items-center gap-1 text-primary">
            View adverse media
            <ExternalLink size={11} aria-hidden />
          </span>
        </Link>
      )}

      {alert.screening && (
        <Link
          to={`/screening/screenings/${alert.screening.public_id}`}
          className={cn(
            'flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-xs transition-colors',
            // Tint by score band so the chip's severity matches the
            // screening verdict at a glance — same palette the
            // ScoreBandBadge in the screening modal uses.
            alert.screening.score_band === 'block'
              ? 'border-status-failed/40 bg-status-failed/5 hover:border-status-failed'
              : alert.screening.score_band === 'review'
                ? 'border-status-pending/40 bg-status-pending/5 hover:border-status-pending'
                : 'border-border bg-muted hover:border-fg/40',
          )}
          title={
            alert.screening.entity_name
              ? `Open screening — ${alert.screening.entity_name}`
              : 'Open originating screening'
          }
        >
          <span className="flex flex-wrap items-center gap-2 text-fg">
            <ShieldAlert
              size={12}
              className={cn(
                'shrink-0',
                alert.screening.score_band === 'block'
                  ? 'text-status-failed'
                  : alert.screening.score_band === 'review'
                    ? 'text-status-pending'
                    : 'text-muted-fg',
              )}
              aria-hidden
            />
            <span className="font-medium">Screening match</span>
            <span className="font-mono text-muted-fg">{alert.screening.list_code}</span>
            <span className="capitalize">
              {alert.screening.score_band} ·{' '}
              <span className="tabular-nums">
                {(alert.screening.composite_score * 100).toFixed(1)}%
              </span>
            </span>
            {alert.screening.entity_name && (
              <span className="text-muted-fg">— {alert.screening.entity_name}</span>
            )}
          </span>
          <span className="flex items-center gap-1 text-primary">
            View screening
            <ExternalLink size={11} aria-hidden />
          </span>
        </Link>
      )}
    </header>
  )
}
